import { useQueryClient } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IMyAssignedTask } from "../../../../@types/Note";
import { Dots } from "../../../../assets/icons/Dots";
import { ReactComponent as NoTasksImage } from "../../../../assets/images/woman-computer.svg";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { ActionContainer } from "../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { Tag } from "../../../../components/Tag";
import {
  invalidateAllNoteCaches,
  useToggleNoteComplete,
  useToggleNoteUrgent,
} from "../../../../hooks/mutations/notes";
import { useMyAssignedTasks } from "../../../../hooks/queries/notes";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { DeleteNoteModal } from "../../../PatientPage/components/PatientNotesTable/components/DeleteNoteModal";
import { EditNoteModal } from "../../../PatientPage/components/PatientNotesTable/components/EditNoteModal";
import { ViewNoteModal } from "../../../PatientPage/components/PatientNotesTable/components/ViewNoteModal";
import {
  AddTaskButton,
  EmptyStateContent,
  HeaderContainer,
  NoTasksMessage,
  SectionWrapper,
  TableContainer,
  TableInnerContainer,
  TagContainer,
} from "./MyTasksTable.styles";
import { myTasksColumns } from "./myTasksTableColumns";

// EN-5191: ids, not labels. The menu's display text is translated at render time, so the
// switch below has to match on something the language cannot change.
const MENU_EDIT = "edit";
const MENU_DELETE = "delete";
const MENU_GO_TO_PATIENT = "goToPatient";
const MENU_MARK_URGENT = "markUrgent";
const MENU_MARK_NOT_URGENT = "markNotUrgent";
const MENU_MARK_COMPLETE = "markComplete";
const MENU_MARK_NOT_COMPLETE = "markNotComplete";

// Zebra-striped pink for active urgent task rows. Same pair as the patient
// page alerts table so the visual language is consistent.
const URGENT_ROW_HIGHLIGHT_COLORS = [
  "rgba(250, 209, 217, 0.48)",
  "rgba(250, 209, 217, 0.28)",
];

function renderCompleteCell(task: IMyAssignedTask, t: TFunction) {
  if (task.isCompleted) {
    return task.completedAt ? getLongFormattedDate(task.completedAt) : "-";
  }
  return (
    <TagContainer>
      <Tag variant="error">{t("myTasks_active")}</Tag>
    </TagContainer>
  );
}

function renderPriorityCell(task: IMyAssignedTask, t: TFunction) {
  if (task.isUrgent && !task.isCompleted) {
    return (
      <TagContainer>
        <Tag variant="error">{t("myTasks_urgent")}</Tag>
      </TagContainer>
    );
  }
  return "-";
}

type MyTasksTableProps = {
  onAddTaskClick: () => void;
};

export function MyTasksTable({ onAddTaskClick }: MyTasksTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const {
    tasks,
    isFetching,
    isRefetching,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useMyAssignedTasks();

  const { mutateAsync: toggleUrgent } = useToggleNoteUrgent();
  const { mutateAsync: toggleComplete } = useToggleNoteComplete();

  const handleDeleteTask = (noteId: number) => {
    openModal(
      <DeleteNoteModal
        noteId={noteId}
        setNotification={(message, type) => showToast(message, type)}
        onClose={() => {
          // Same record lives in the patient page note list and in the
          // single-note cache used by ViewNoteModal — invalidate everything.
          invalidateAllNoteCaches(queryClient);
          refetch();
          closeModal();
        }}
      />,
      {
        width: "auto",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const handleEditTask = (noteId: number) => {
    openModal(
      <EditNoteModal
        noteId={noteId}
        setNotification={(message, type) => showToast(message, type)}
        onClose={() => {
          invalidateAllNoteCaches(queryClient);
          refetch();
          closeModal();
        }}
      />,
      {
        width: "auto",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const handleViewTask = (noteId: number) => {
    openModal(<ViewNoteModal noteId={noteId} onEditClick={handleEditTask} />, {
      width: "auto",
      height: "auto",
      showCloseButton: true,
    });
  };

  const handleToggleUrgent = async (
    noteId: number,
    currentlyUrgent: boolean
  ) => {
    try {
      await toggleUrgent({ noteId, isUrgent: !currentlyUrgent });
      showToast(
        currentlyUrgent
          ? t("myTasks_toasts_unmarkedUrgent")
          : t("myTasks_toasts_markedUrgent"),
        "success"
      );
    } catch {
      showToast(t("myTasks_toasts_urgentFailed"), "error");
    }
  };

  const handleToggleComplete = async (
    noteId: number,
    currentlyComplete: boolean
  ) => {
    try {
      await toggleComplete({ noteId, isCompleted: !currentlyComplete });
      showToast(
        currentlyComplete
          ? t("myTasks_toasts_markedNotComplete")
          : t("myTasks_toasts_markedComplete"),
        "success"
      );
    } catch {
      showToast(t("myTasks_toasts_completionFailed"), "error");
    }
  };

  const highlightedRows = useMemo(
    () => tasks.map((t) => t.isUrgent && !t.isCompleted),
    [tasks]
  );

  const rows = useMemo(() => {
    return tasks.map((task: IMyAssignedTask) => {
      // Edit/Delete are creator-only on the server. Hide them from the menu
      // for assignees so they don't land on a save that 404s.
      const options = [
        task.isUrgent ? MENU_MARK_NOT_URGENT : MENU_MARK_URGENT,
        task.isCompleted ? MENU_MARK_NOT_COMPLETE : MENU_MARK_COMPLETE,
        ...(task.editable ? [MENU_EDIT, MENU_DELETE] : []),
        MENU_GO_TO_PATIENT,
      ].map((id) => ({ id, label: t(`myTasks_menu_${id}`) }));

      const patientLabel = task.patientName || `#${task.patientId}`;

      return {
        id: task.id,
        patientId: task.patientId,
        patient: patientLabel,
        notes: task.notes,
        complete: renderCompleteCell(task, t),
        time: getLongFormattedDate(task.time),
        priority: renderPriorityCell(task, t),
        isUrgent: task.isUrgent,
        isCompleted: task.isCompleted,
        editable: task.editable,
        action: (
          <div onClick={(e) => e.stopPropagation()} role="presentation">
            <ActionContainer className="actionContainer">
              <Dropdown
                trigger={
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                }
                value=""
                onOpenChange={(val: boolean) => setIsDropDownOpen(val)}
                onValueChange={(val: string) => {
                  switch (val) {
                    case MENU_EDIT:
                      handleEditTask(task.id);
                      break;
                    case MENU_DELETE:
                      handleDeleteTask(task.id);
                      break;
                    case MENU_GO_TO_PATIENT:
                      navigate(`/patients/${task.patientId}/notes`);
                      break;
                    case MENU_MARK_URGENT:
                    case MENU_MARK_NOT_URGENT:
                      handleToggleUrgent(task.id, task.isUrgent);
                      break;
                    case MENU_MARK_COMPLETE:
                    case MENU_MARK_NOT_COMPLETE:
                      handleToggleComplete(task.id, task.isCompleted);
                      break;
                    default:
                      break;
                  }
                }}
              >
                {options.map((option) => (
                  <DropdownTextItem
                    key={option.id}
                    value={option.id}
                    text={option.label}
                  />
                ))}
              </Dropdown>
            </ActionContainer>
          </div>
        ),
      };
    });
  }, [tasks, t]);

  const handleRowClick = (row: { id: number; editable: boolean }) => {
    if (isDropDownOpen) return;
    if (row.editable) {
      handleEditTask(row.id);
    } else {
      handleViewTask(row.id);
    }
  };

  const isInitialLoading = isFetching && !isRefetching && !isFetchingNextPage;

  return (
    <SectionWrapper>
      <HeaderContainer>
        <PageTitle>{t("myTasks_title")}</PageTitle>
        <AddTaskButton onClick={onAddTaskClick}>
          {t("myTasks_addTask")}
        </AddTaskButton>
      </HeaderContainer>

      {isInitialLoading && (
        <TableContainer>
          <TableInnerContainer>
            <TableSkeleton columnsNumber={6} rowsNumber={5} />
          </TableInnerContainer>
        </TableContainer>
      )}

      {!isInitialLoading && isError && (
        <p>
          <Trans
            i18nKey="patientList_tasks_loadError"
            components={{
              retry: <RetryButton onClick={() => refetch()} />,
            }}
          />
        </p>
      )}

      {!isInitialLoading && !isError && rows.length === 0 && (
        <EmptyStateContent>
          <NoTasksImage width={181} height={158} />
          <NoTasksMessage>{t("patientList_tasks_empty")}</NoTasksMessage>
        </EmptyStateContent>
      )}

      {!isInitialLoading && !isError && rows.length > 0 && (
        <TableContainer>
          <TableInnerContainer>
            <InfiniteTable
              data={rows}
              columns={myTasksColumns}
              onRowClick={handleRowClick}
              highlightedRows={highlightedRows}
              highlightColor={URGENT_ROW_HIGHLIGHT_COLORS}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              skeletonColumns={6}
              skeletonRows={5}
            />
          </TableInnerContainer>
        </TableContainer>
      )}
    </SectionWrapper>
  );
}
